import { useTranslation } from '../presentation/common/useTranslation.js';
import FeatureIcon from '../presentation/common/FeatureIcon';
function NewsIcon(props) { return <FeatureIcon name="news" {...props} />; }
import { presentationText } from '../presentation/text';
import GoodvaleWordmark from './GoodvaleWordmark';
import HeroBusinessCard from './HeroBusinessCard';
import HeroClass from './HeroClass';
import HeroRank from './HeroRank';
import { getFullImageUrl } from '../services/api/client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';

import { ClassIcon } from './GameIcon';
import { 
  Shield, 
  HelpCircle, 
  Map, 
  LogOut, 
  LogIn, 
  UserPlus, 
  Gavel, 
  Zap, 
  Award,
  Sparkles,
  User, 
  BookOpen,
  ChevronDown,
  Bell,
  Scale,
  Sliders,
  Settings,
  Menu,
  MessageSquare,
  Share2,
  Star,
  History,
} from 'lucide-react';
import { Compass, Feather, CalendarDays } from 'lucide-react';
import '../styles/world.css';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getCurrentUser, getCurrentUserProfile, logoutUser } from '../services/api/auth';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { getNotifications, getUnreadCount, markAllAsRead, markAsRead } from '../services/api/notifications';
import { getNotificationNavigationPath } from '../utils/notificationNavigation';
import { getTranslatedNotification } from '../utils/formatNotification';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import { PresentationModeSwitch } from '../presentation/PresentationProvider';
import ShareModal from './ShareModal';
import GoodvaleLogo from './GoodvaleLogo';
import PlayerProgress from './PlayerProgress';
import LevelUpPopup from './LevelUpPopup';
import HeroicApprovalPopup from './HeroicApprovalPopup';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSystemAdmin } = useCurrentUser();
  const [user, setUser] = useState(getCurrentUser());
  const [cardProfile, setCardProfile] = useState(null);
  const [approvalState, setApprovalState] = useState({ level: null, pending: true });
  const onApprovalStateChange = useCallback(state => setApprovalState(state), []);
  useEffect(() => {
    let active = true;
    let pending = false;
    const refresh = async () => {
      if (!user || pending) return;
      pending = true;
      try { const profile = await getCurrentUserProfile(); if (active) setCardProfile(profile); }
      catch { /* Retry on the next refresh. */ }
      finally { pending = false; }
    };
    refresh();
    const interval = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    const updatePreferences = event => {
      setCardProfile(current => current ? { ...current, levelUpPopupsEnabled: event.detail } : current);
    };
    window.addEventListener('level-up-preference', updatePreferences);
    const updateApprovalPreference = event => {
      setCardProfile(current => current ? { ...current, heroicApprovalPopupsEnabled: event.detail } : current);
    };
    window.addEventListener('heroic-approval-preference', updateApprovalPreference);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('level-up-preference', updatePreferences);
      window.removeEventListener('heroic-approval-preference', updateApprovalPreference);
    };
  }, [user, location.pathname]);
  const [helpOpen, setHelpOpen] = useState(false);
  const isSettingsActive = location.pathname.startsWith('/admin');
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellDropdownOpen, setBellDropdownOpen] = useState(false);
  const bellDropdownRef = useRef(null);
  const [profileFlyoutOpen, setProfileFlyoutOpen] = useState(false);
  const profileFlyoutRef = useRef(null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const activeProfile = cardProfile || user;
  const profileName = activeProfile?.username || activeProfile?.name || presentationText('systemCopy.b5451aaa13ba');
  const profileAvatarUrl = activeProfile?.avatarUrl;
  const heroClass = activeProfile?.heroClass;
  const rank = activeProfile?.rank;
  const userLevel = activeProfile?.level ?? 1;
  const profileInitials = profileName ? profileName.slice(0, 2).toUpperCase() : 'AV';

  useEffect(() => {
    if (isSettingsActive) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  const fetchNotificationStats = async () => {
    if (!user) return;
    try {
      const countRes = await getUnreadCount();
      setUnreadCount(countRes || 0);

      const listRes = await getNotifications(1, 5);
      setNotifications(listRes?.data || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  useEffect(() => {
    const handleStorage = () => setUser(getCurrentUser());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    fetchNotificationStats();
    const interval = setInterval(fetchNotificationStats, 30000);
    window.addEventListener('notifications-changed', fetchNotificationStats);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-changed', fetchNotificationStats);
    };
  }, [user]);

  useEffect(() => {
    if (!profileFlyoutOpen && !bellDropdownOpen) return;

    const handlePointerDown = (event) => {
      if (!profileFlyoutRef.current?.contains(event.target)) {
        setProfileFlyoutOpen(false);
      }
      if (!bellDropdownRef.current?.contains(event.target)) {
        setBellDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setProfileFlyoutOpen(false);
        setBellDropdownOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileFlyoutOpen, bellDropdownOpen]);

  const handleLogout = async () => {
    await logoutUser();
    setUser(null);
    navigate('/', { replace: true });
  };

  const navigation = [
    { name: t('world.nav.overview', 'Übersicht'), href: '/dashboard', icon: Compass },
    { name: t('world.nav.heroic', 'Gute Tat'), href: '/heroic-actions', icon: Feather },
    { name: t('world.nav.events', 'Events'), href: '/heroic-actions/events', icon: CalendarDays },
    { name: t('nav.talents', 'Talente'), href: '/talents', icon: Sparkles },
    { name: t('nav.tavern'), href: '/tavern', icon: NewsIcon },
    { name: t('nav.map'), href: '/map', icon: Map },
    { name: t('world.nav.chat', 'Chat'), href: '/chats', icon: MessageSquare },
    { name: t('world.nav.guild', 'Gilde'), href: user?.guildId ? `/guilds/${user.guildId}` : '/guilds', icon: Shield },
    { name: t('world.nav.profile', 'Profil'), href: '/profile', icon: User },
    { name: t('partnerDiscovery.title'), href: '/profile/redemptions', icon: History },
    ...(user?.isTribunalModerator || isSystemAdmin ? [{ name: t('nav.moderation', 'Moderation'), href: '/tribunal', icon: Gavel }] : []),
  ];

  const settingsChildren = [
    ...(isSystemAdmin ? [
      { name: t('nav.system_config', 'System-Konfiguration'), href: '/admin', icon: Sliders }
    ] : []),
  ];

  const helpChildren = [
    { name: t('nav.mechanics', 'Spielmechanik'), href: '/app/mechanics', icon: BookOpen },
    { name: t('nav.categories', 'Kategorien'), href: '/app/categories', icon: Compass },
    { name: t('nav.classes', 'Klassen'), href: '/app/classes', icon: Zap },
    { name: t('nav.ranks', 'Ränge'), href: '/app/ranks', icon: Award },
    { name: t('nav.faq'), href: '/app/faq', icon: HelpCircle },
    { name: t('nav.legal', 'Rechtliches'), href: '/app/legal', icon: Scale },
  ];
  const isHelpActive = helpChildren.some(({ href }) => location.pathname === href);
  const isGuildRoom = location.pathname.startsWith('/guilds/') &&
    location.pathname !== '/guilds/create' &&
    !/^\/guilds\/[^/]+\/group-action-guide\/?$/.test(location.pathname);

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-zinc-950">
      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-[90] md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <nav className={cn(
        "world-sidebar w-full md:w-56 shrink-0 bg-[#06101a] border-r border-amber-200/15 flex flex-col fixed inset-y-0 left-0 z-[100] transform transition-transform duration-300 md:translate-x-0 md:static md:z-40 md:flex h-full shadow-[14px_0_45px_rgba(0,0,0,.35)] before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_8%,rgba(251,191,36,.07),transparent_28%),linear-gradient(180deg,rgba(12,28,42,.7),rgba(3,10,16,.96))]",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="relative z-10 flex items-center gap-3 border-b border-amber-200/10 px-2 py-[14px] md:px-5 md:py-5">
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="p-2 text-zinc-400 hover:text-zinc-200 md:hidden"
            aria-label="Close menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-3 group" onClick={() => setSidebarOpen(false)}>
            <GoodvaleLogo className="h-9 w-9 rounded-md group-hover:scale-105 transition-transform" />
            <div>
              <h1><GoodvaleWordmark className="w-28" /></h1>            
            </div>
          </Link>
        </div>

        <div className="relative z-10 mt-3 min-h-0 flex-1 space-y-1 overflow-y-auto px-3 [scrollbar-width:thin] [scrollbar-color:rgba(251,191,36,.2)_transparent]">
          {navigation.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === '/profile' || item.href === '/heroic-actions' && location.pathname.startsWith('/heroic-actions/events')}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => {
                const isGuildActive = item.href.includes('/guilds') && location.pathname.startsWith('/guilds');
                const active = isActive || isGuildActive;
                return cn(
                  'group relative flex items-center gap-4 border border-transparent px-3 py-2.5 font-serif text-[15px] tracking-wide transition-all duration-200 before:absolute before:inset-y-1 before:left-0 before:w-px before:bg-amber-200 before:opacity-0 before:shadow-[0_0_10px_#fde68a]',
                  active
                    ? 'border-amber-200/20 bg-[linear-gradient(90deg,rgba(180,120,30,.28),rgba(30,35,45,.24))] text-amber-50 shadow-[inset_3px_0_8px_rgba(251,191,36,.12)] before:opacity-100'
                    : 'text-violet-200/75 hover:border-white/5 hover:bg-white/[.035] hover:text-violet-100'
                );
              }}
            >
              <item.icon className="h-6 w-6 shrink-0 text-violet-300/80 transition-colors group-hover:text-violet-200 group-aria-[current=page]:text-[#f2cf86] group-aria-[current=page]:drop-shadow-[0_0_8px_#caa45366]" />
              {item.name}
            </NavLink>
          ))}

          {/* Einstellungen (Settings) collapsible group */}
          {settingsChildren.length > 0 && (
            <div>
              <button
                onClick={() => setSettingsOpen(!settingsOpen)}
                className={cn(
                  'flex w-full items-center justify-between border border-transparent px-3 py-2.5 font-serif text-[15px] tracking-wide transition-colors',
                  settingsOpen || isSettingsActive
                    ? 'bg-white/[.035] text-amber-50'
                    : 'text-violet-200/75 hover:bg-white/[.035] hover:text-violet-100'
                )}
                aria-expanded={settingsOpen}
              >
                <span className="flex items-center gap-3">
                  <Settings className="w-4 h-4" />
                  {t('nav.settings', 'Einstellungen')}
                </span>
                <ChevronDown
                  className={cn(
                    'w-3.5 h-3.5 transition-transform duration-200',
                    settingsOpen && 'rotate-180'
                  )}
                />
              </button>
              {settingsOpen && (
                <div className="ml-3 pl-3 border-l border-zinc-800 space-y-0.5 mt-0.5">
                  {settingsChildren.map((child) => (
                    <NavLink
                      key={child.href}
                      to={child.href}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 border-l border-transparent px-3 py-2 font-serif text-sm tracking-wide transition-colors',
                          isActive
                            ? 'border-amber-300 bg-amber-500/10 text-amber-50'
                            : 'text-violet-200/65 hover:bg-white/[.035] hover:text-violet-100'
                        )
                      }
                    >
                      <child.icon className="w-4 h-4" />
                      {child.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hilfe (Help) collapsible group */}
          <div>
            <button
              type="button"
              onClick={() => setHelpOpen(!helpOpen)}
              className={cn(
                'group relative flex w-full items-center justify-between gap-4 border border-transparent px-3 py-2.5 font-serif text-[15px] tracking-wide transition-all duration-200 before:absolute before:inset-y-1 before:left-0 before:w-px before:bg-amber-200 before:opacity-0 before:shadow-[0_0_10px_#fde68a]',
                isHelpActive
                  ? 'border-amber-200/20 bg-[linear-gradient(90deg,rgba(180,120,30,.28),rgba(30,35,45,.24))] text-amber-50 shadow-[inset_3px_0_8px_rgba(251,191,36,.12)] before:opacity-100'
                  : 'text-violet-200/75 hover:border-white/5 hover:bg-white/[.035] hover:text-violet-100'
              )}
              aria-expanded={helpOpen}
            >
              <span className="flex items-center gap-4">
                <BookOpen className={cn('h-6 w-6 shrink-0 text-violet-300/80 transition-colors group-hover:text-violet-200', isHelpActive && 'text-[#f2cf86] drop-shadow-[0_0_8px_#caa45366]')} />
                {t('nav.help', 'Hilfe')}
              </span>
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 transition-transform duration-200',
                  helpOpen && 'rotate-180'
                )}
              />
            </button>
            {helpOpen && (
              <div className="ml-3 pl-3 border-l border-zinc-800 space-y-0.5 mt-0.5">
                {helpChildren.map((child) => (
                  <NavLink
                    key={child.href}
                    to={child.href}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 border-l border-transparent px-3 py-2 font-serif text-sm tracking-wide transition-colors',
                        isActive
                          ? 'border-amber-300 bg-amber-500/10 text-amber-50'
                          : 'text-violet-200/65 hover:bg-white/[.035] hover:text-violet-100'
                      )
                    }
                  >
                    <child.icon className="w-4 h-4" />
                    {child.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="relative z-10 mt-auto space-y-2 border-t border-amber-200/10 p-4">
          {user ? (
            <>
              <button 
                onClick={() => {
                  setIsShareOpen(true);
                  setSidebarOpen(false);
                }}
                className="flex items-center gap-3 px-3 py-2.5 w-full text-sm font-medium text-indigo-400 hover:text-indigo-300 hover:bg-indigo-950/20 border border-transparent hover:border-indigo-900/40 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                {t('share.title', 'Freunde einladen')}
              </button>
              <button 
                onClick={() => {
                  handleLogout();
                  setSidebarOpen(false);
                }}
                className="flex items-center gap-3 px-3 py-2.5 w-full text-sm font-medium text-rose-400 hover:text-rose-300 hover:bg-rose-950/20 border border-transparent hover:border-rose-900/40 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <div className="space-y-2">
              <Link 
                to="/login"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center justify-center gap-2 px-3 py-2 w-full text-xs font-semibold text-zinc-300 bg-zinc-800 hover:bg-zinc-700 transition-colors"
              >
                <LogIn className="w-3.5 h-3.5" />
                {t('common.login')}
              </Link>
              <Link 
                to="/register"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center justify-center gap-2 px-3 py-2 w-full text-xs font-bold text-zinc-950 bg-zinc-100 hover:bg-white transition-colors"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {t('common.register')}
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main data-page-scroll className={cn(
        "world-app-main relative flex-1 min-w-0 flex flex-col h-full",
        (location.pathname === '/chats' || isGuildRoom)
          ? "overflow-hidden"
          : "overflow-y-auto"
      )}>
        {/* Topbar */}
        <header className="world-topbar sticky top-0 z-40 h-16 shrink-0 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-2.5 md:gap-4">
            <button
              type="button"
              onClick={() => setSidebarOpen(open => !open)}
              className="p-2 -ml-2 text-zinc-400 hover:text-zinc-200 md:hidden transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
            {user ? (
              <>
                {/* Level Badge / Progress - Leftmost in this group */}
                <PlayerProgress profile={activeProfile} variant="nav" />

                {/* Notification Bell Dropdown */}
                <div className="relative" ref={bellDropdownRef}>
                  <button
                    onClick={() => {
                      setBellDropdownOpen(!bellDropdownOpen);
                      setProfileFlyoutOpen(false);
                    }}
                    className="world-icon-button relative"
                    aria-expanded={bellDropdownOpen}
                  >
                    <Bell className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center bg-rose-500 text-[10px] font-bold text-white rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </button>

                  {bellDropdownOpen && (
                    <div className="world-notification-menu absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] z-50 py-2">
                      <div className="px-4 py-2 border-b border-zinc-800 flex justify-between items-center">
                        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
                          {t('notifications.title', 'Benachrichtigungen')}
                        </span>
                        {unreadCount > 0 && (
                          <button
                            onClick={async () => {
                              await markAllAsRead();
                              fetchNotificationStats();
                            }}
                            className="text-[10px] text-indigo-400 hover:text-indigo-300 font-medium cursor-pointer"
                          >
                            {t('notifications.mark_all_read', 'Alle gelesen')}
                          </button>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-6 text-center text-xs text-zinc-500">
                            {t('notifications.empty', 'Keine neuen Benachrichtigungen')}
                          </div>
                        ) : (
                          notifications.map((n) => {
                            const { title, message } = getTranslatedNotification(n, t);
                            return (
                              <div
                                key={n.id}
                                onClick={async () => {
                                  if (!n.isRead) {
                                    await markAsRead(n.id);
                                  }
                                  setBellDropdownOpen(false);
                                  fetchNotificationStats();
                                  const targetPath = getNotificationNavigationPath(n, user, isSystemAdmin);
                                  if (targetPath) {
                                    navigate(targetPath);
                                  }
                                }}
                                className={cn(
                                  'world-notification-item px-4 py-2.5 cursor-pointer text-left transition-colors',
                                  !n.isRead && 'bg-zinc-800/20'
                                )}
                              >
                                <p className="text-xs font-semibold text-zinc-200 truncate">{title}</p>
                                <p className="text-[11px] text-zinc-400 line-clamp-2 mt-0.5">{message}</p>
                                <span className="text-[9px] text-zinc-500 mt-1 block">
                                  {new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="px-4 py-2 border-t border-zinc-800 text-center">
                        <Link
                          to="/notifications"
                          onClick={() => setBellDropdownOpen(false)}
                          className="text-xs text-zinc-400 hover:text-zinc-200 font-medium block"
                        >
                          {t('notifications.view_all', 'Alle anzeigen')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Square Profile Picture Box & Interactive Flyout Menu Trigger */}
                <div className="relative" ref={profileFlyoutRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setProfileFlyoutOpen(!profileFlyoutOpen);
                      setBellDropdownOpen(false);
                    }}
                    className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 bg-zinc-900 border-2 border-amber-400/60 hover:border-amber-300 rounded-md overflow-hidden flex items-center justify-center text-xs font-bold text-amber-200 transition-colors shadow-md focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                    aria-label="Profil Menü öffnen"
                    aria-expanded={profileFlyoutOpen}
                  >
                    {profileAvatarUrl && !avatarFailed ? (
                      <img
                        src={getFullImageUrl(profileAvatarUrl)}
                        alt={profileName}
                        className="w-full h-full object-cover"
                        onError={() => setAvatarFailed(true)}
                      />
                    ) : (
                      <span>{profileInitials}</span>
                    )}
                  </button>

                  {profileFlyoutOpen && (
                      <div className="world-profile-flyout">
                        {/* Profile Header Summary (Name, Hero Class, Rank) */}
                        <div className="world-profile-flyout-header">
                          <div className="w-12 h-12 shrink-0 bg-zinc-900 border-2 border-amber-400/70 rounded-md overflow-hidden flex items-center justify-center text-sm font-bold text-amber-200">
                            {profileAvatarUrl && !avatarFailed ? (
                              <img
                                src={getFullImageUrl(profileAvatarUrl)}
                                alt={profileName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span>{profileInitials}</span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="world-profile-flyout-name truncate">
                              {t('app.adventurer', { name: profileName })}
                            </h4>
                            <div className="world-profile-flyout-sub">
                              {heroClass && <HeroClass heroClass={heroClass} />}
                              {heroClass && rank && <span>•</span>}
                              {rank && <HeroRank rank={rank} />}
                            </div>
                          </div>
                        </div>

                        {/* Preferences */}
                        <div className="world-profile-flyout-section">
                          <div className="world-profile-flyout-row">
                            <span className="font-medium">{t('settings.language', 'Sprache')}</span>
                            <LanguageSwitcher />
                          </div>
                          <div className="world-profile-flyout-row">
                            <span className="font-medium">{t('settings.theme', 'Design')}</span>
                            <ThemeSwitcher />
                          </div>
                          <div className="world-profile-flyout-row world-profile-flyout-presentation">
                            <span className="font-medium">{t('presentation.gameMode')}</span>
                            <PresentationModeSwitch />
                          </div>
                          <button
                            onClick={() => {
                              setIsShareOpen(true);
                              setProfileFlyoutOpen(false);
                            }}
                            className="world-profile-flyout-share"
                          >
                            <span className="flex items-center gap-2">
                              <Share2 className="w-4 h-4" />
                              {t('share.title', 'Freunde einladen')}
                            </span>
                          </button>
                        </div>

                        {/* Navigation Links & Logout */}
                        <div className="pt-3 space-y-1">
                          <Link
                            to="/profile"
                            onClick={() => setProfileFlyoutOpen(false)}
                            className="world-profile-flyout-item"
                          >
                            <User className="w-4 h-4 text-amber-500" />
                            {t('world.nav.profile', 'Profil ansehen')}
                          </Link>
                          <button
                            onClick={() => {
                              setProfileFlyoutOpen(false);
                              handleLogout();
                            }}
                            className="world-profile-flyout-logout"
                          >
                            <LogOut className="w-4 h-4" />
                            {t('nav.logout', 'Abmelden')}
                          </button>
                        </div>
                      </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-3.5 py-1.5 text-xs font-medium text-zinc-300 hover:text-white border border-zinc-800 hover:bg-zinc-900 transition-colors"
                >
                  {t('common.login')}
                </Link>
                <Link
                  to="/register"
                  className="px-3.5 py-1.5 text-xs font-bold bg-zinc-100 text-zinc-950 hover:bg-white transition-colors"
                >
                  {t('common.register')}
                </Link>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div data-page-scroll className={cn(
          "world-app-content flex-1 min-w-0 flex flex-col",
          (location.pathname === '/chats' || isGuildRoom)
            ? "overflow-hidden p-0 h-full"
            : "world-app-content-scroll p-4 sm:p-6 lg:p-10"
        )}>
          <Outlet />
        </div>
      </main>
      <ShareModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)} />
      {user && <HeroicApprovalPopup userId={cardProfile?.userId} level={cardProfile?.level} enabled={cardProfile?.heroicApprovalPopupsEnabled !== false} onStateChange={onApprovalStateChange} />}
      {user && <LevelUpPopup profile={cardProfile} blocked={approvalState.pending || approvalState.level !== cardProfile?.level} />}
    </div>
  );
}
